import classNames from 'classnames'
import { FormikProps, withFormik, type FormikErrors } from 'formik'
import { t } from 'i18next'
import React, { FormEventHandler, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { CountryCode } from '@app/consts/Countries'

import { Form } from '@lib/formik'

import { useReferral } from '@pages/Referral/Referral'

import { useSessionEntity } from '@context/EntityContext'
import { useModal } from '@context/ModalsContext'
import { useArabicSessionLanguage, useSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Caption1, Heading6 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Button } from '@ui/components/Button'
import { Chip } from '@ui/components/Chip'
import { PopoverInput } from '@ui/components/Popover'
import { Radio } from '@ui/components/Radio'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { FlagIcon } from '@ui/icons/flags'
import { IconFCType, IconProps } from '@ui/icons/types'

import { useApiClient } from '@utils/ApiClient'
import {
  AddressEntryMode,
  getInitialAddressEntryMode,
  validateManualAddressFields,
} from '@utils/address.utils'
import { ClientApiClient } from '@utils/clientApi'
import {
  TickmillCompaniesEnum,
  isEitherASTypeOrSCType,
  isTickmillEUType,
  isTickmillUKType,
} from '@utils/companyName.utils'
import { IBPattern } from '@utils/formValidation'

import { TruliooEIdData } from '@model/CountryDto'
import { LeadDto } from '@model/LeadDto'
import { NameDto } from '@model/NameDto'
import { RegulatorOptionDto } from '@model/RegulatorOptionDto'

import { createFormCheckBoxField } from '@global/formField/FormCheckBoxField'
import { createFormField } from '@global/formField/FormField'

import { useLinksConfig } from '@hooks/useLinksConfig'
import { useMixedText } from '@hooks/useMixedText'
import { safeLocalStorage } from '@hooks/useStorage'

import { Box } from '@shared/components/Box'
import { FieldWrapper } from '@shared/components/FieldWrapper'
import { InformationModal } from '@shared/modals/Information.modal'

import { useProducts } from '../../../hooks/useProducts'
import { ProductSelection } from './ProductSelection'
import { ResidentialAddressFields } from './ResidentialAddressFields'
import { useIbCodeValidation } from './useIbCodeValidation'

import sharedStyles from '../PersonalDetailsPage.module.scss'
import styles from './PersonalDetailsStep4Form.module.scss'

const FormField = createFormField<PersonalDetailsStep4FormValues>()
const FormCheckBoxField = createFormCheckBoxField<PersonalDetailsStep4FormValues>()

export interface PersonalDetailsStep4FormValues {
  nationalities: NameDto<string>[]
  address: string
  primaryAddressPostalCode: string
  primaryAddressCity: string
  primaryAddressState: string
  taxId: string
  mainIbCode: string
  affiliateToken: string
  taxIdNotAvailable: boolean
  streetHouseNumberNotAvailable: boolean
  streetHouseNumber: string
  addressEntryMode: AddressEntryMode
  isPoliticallyExposedPerson: boolean
  taxIdUnavailableReasonId?: number
  nationalityId: string
  personalId?: string
  truliooCountryData: TruliooEIdData[]
  selectedTruliooCountry?: number
  personalIdTypeId: string | null
  productIds?: number[]
}

export interface OuterProps {
  lead?: LeadDto
  initialValues: PersonalDetailsStep4FormValues | undefined
  nationalities: NameDto<CountryCode>[]
  taxReasons: NameDto[]
  entity: TickmillCompaniesEnum
  country?: NameDto<string>
  regulatorOptions?: RegulatorOptionDto[]

  onSubmit(values: PersonalDetailsStep4FormValues): void
  requireTaxId: boolean
  isStocksOnlyClient?: boolean
  availableProducts: any[]
  productsLoading: boolean
}

const pepDefinitions = [
  {
    title:
      'Sign up.Politically Exposed Person.Natural Persons Entrusted with Prominent Public Functions:',
    items: [
      'Sign up.Politically Exposed Person.Heads of state, heads of government, ministers, and deputy or assistant ministers',
      'Sign up.Politically Exposed Person.Members of parliaments or similar legislative bodies',
      'Sign up.Politically Exposed Person.Members of supreme courts, constitutional courts, or other high-level judicial bodies whose decisions are not subject to further appeal',
      'Sign up.Politically Exposed Person.Members of courts of auditors or central banks',
      'Sign up.Politically Exposed Person.Ambassadors, chargés d’affaires, and high-ranking officers in the armed forces',
      'Sign up.Politically Exposed Person.Members of administrative, management, or supervisory bodies of state-owned enterprises',
    ],
    itemsUK: [
      'Sign up.Politically Exposed Person.Heads of state, heads of government, ministers, and deputy or assistant ministers',
      'Sign up.Politically Exposed Person.Members of parliaments or similar legislative bodies',
      'Sign up.Politically Exposed Person.Members of supreme courts, constitutional courts, or other high-level judicial bodies whose decisions are not subject to further appeal UK',
      'Sign up.Politically Exposed Person.Members of courts of auditors or central banks',
      'Sign up.Politically Exposed Person.Ambassadors, chargés d’affaires, and high-ranking officers in the armed forces',
      'Sign up.Politically Exposed Person.Members of administrative, management, or supervisory bodies of state-owned enterprises',
      'Sign up.Politically Exposed Person.Directors, deputy directors and members of the board or equivalent function of an international organisation',
    ],
  },
]

const PepContent = () => {
  const entity = useSessionEntity()

  return (
    <div>
      <Body1>
        <Trans
          i18nKey='Sign up.Politically Exposed Person.A Politically Exposed Person (PEP) is defined as:'
          components={{ strong: <Body1 medium /> }}
        />
      </Body1>
      <ol className={styles.pepOrderedList}>
        {pepDefinitions.map((definition, index) => (
          <li key={index} className={styles.pepOrderedListItem}>
            <Body1 medium>{t(definition.title)}</Body1>
            <ul className={styles.pepUnOrderedList}>
              {(isTickmillUKType(entity) ? definition.itemsUK : definition.items).map(
                (item, subIndex) => (
                  <li key={subIndex} className={styles.pepUnOrderedListItem}>
                    {t(item)}
                  </li>
                )
              )}
            </ul>
          </li>
        ))}

        <li className={styles.pepOrderedListItem}>
          <Trans
            i18nKey={
              isTickmillUKType(entity)
                ? 'Sign up.Politically Exposed Person.Immediate family members of PEPs UK'
                : 'Sign up.Politically Exposed Person.Immediate family members of PEPs'
            }
            components={{ strong: <Body1 medium /> }}
          />
        </li>
        <li className={styles.pepOrderedListItem}>
          <Trans
            i18nKey='Sign up.Politically Exposed Person.Individuals closely associated with a PEP'
            components={{ strong: <Body1 medium /> }}
          />
        </li>
      </ol>
      <Trans
        i18nKey='Sign up.Politically Exposed Person.Note'
        components={{ strong: <Body1 medium /> }}
      />
    </div>
  )
}

type PersonalDetailsStep4FormProps = FormikProps<PersonalDetailsStep4FormValues> & OuterProps

const PersonalDetailsStep4FormUI: React.FC<PersonalDetailsStep4FormProps> = (props) => {
  const { isSubmitting, lead } = props
  const {
    errors,
    nationalities,
    initialValues,
    handleSubmit,
    setFieldValue,
    isValid,
    setFieldTouched,
    values,
    validateForm,
    requireTaxId,
    taxReasons,
    isStocksOnlyClient,
    availableProducts,
    productsLoading,
  } = props
  const { truliooCountryData, selectedTruliooCountry } = values
  const locale = useSessionLanguage()
  const { t } = useTranslation()
  const { parseMixedText } = useMixedText()
  const apiClient = useApiClient(ClientApiClient)
  const { links } = useLinksConfig()
  const entity = useSessionEntity()
  const referral = useReferral()
  const { setSelectedProductIds } = useProducts()

  const isASOrSC = useMemo(() => isEitherASTypeOrSCType(entity), [entity])
  const isEUOrUK = useMemo(() => isTickmillEUType(entity) || isTickmillUKType(entity), [entity])

  const [personalIdLoading, setPersonalIdLoading] = useState(false)
  const [ibCodeError, setIBCodeError] = useState<string>()
  const [hasIBCode, setHasIBCode] = useState(false)
  const [isIbCodeValidating, setIsIbCodeValidating] = useState(false)
  const [referralCodeCollapsed, setReferralCodeCollapsed] = useState(false)

  useIbCodeValidation({
    isASOrSC,
    isEUOrUK,
    mainIbCode: values.mainIbCode,
    mainIbCodeError: errors.mainIbCode || '',
    entity,
    apiClient,
    setIsIbCodeValidating,
    setIBCodeError,
    setHasIBCode,
    setReferralCodeCollapsed,
    setFieldValue,
    t,
  })

  const pepInfoModal = useModal(InformationModal, {
    title: t('Sign up.Politically Exposed Person.Title'),
    secondaryLabel: t('Got it!'),
    description: () => <PepContent />,
  })

  const onRemoveNation = (nation: NameDto<string>) => {
    const newNations = values.nationalities.filter((x) => x.id !== nation.id)
    setFieldValue('nationalities', newNations)
    if (!newNations.length) {
      resetFieldValues()
    } else {
      updateFieldValues(newNations)
    }
  }

  const updateFieldValues = (newNations: NameDto<string>[]) => {
    if (newNations.some((nation) => nation.id === lead?.country.id)) {
      getTruliooCountryData(lead?.country.id)
    } else {
      resetFieldValues()
      const newNationality = newNations[0].id
      setFieldValue('nationalityId', newNationality)
    }
  }

  const resetFieldValues = () => {
    setFieldValue('nationalityId', '')
    setFieldValue('selectedTruliooCountry', undefined)
    setFieldValue('truliooCountryData', [])
    setFieldValue('personalId', '')
    removeHasAdditionalId()
  }

  const onSubmitForm: FormEventHandler = (e) => {
    e.preventDefault()
    validateForm()
    handleSubmit()
  }

  const onChangeTaxAvailability = (checked: boolean) => {
    setFieldValue('taxIdNotAvailable', checked)
    if (checked) {
      setFieldValue('taxId', '')
      setFieldTouched('taxId', false, false)
    } else {
      setFieldValue('taxIdUnavailableReasonId', undefined)
    }
  }

  const removeHasAdditionalId = () => safeLocalStorage.removeItem('hasAdditionalId')

  const getTruliooCountryData = async (nationalityId?: string) => {
    try {
      setPersonalIdLoading(true)

      if (!isASOrSC || !lead?.id) {
        return
      }

      setFieldValue('truliooCountryData', [])
      setFieldValue('selectedTruliooCountry', undefined)

      const data = await apiClient.getTruliooCountries(
        nationalityId || values.nationalityId,
        locale || 'en',
        lead?.id
      )

      if (data.length) {
        const countryToSelect = data[0].id

        setFieldValue('selectedTruliooCountry', countryToSelect)
        setFieldValue('truliooCountryData', data)
        setFieldValue('personalIdTypeId', countryToSelect)

        safeLocalStorage.setItem('hasAdditionalId', 'false')
      } else {
        safeLocalStorage.removeItem('hasAdditionalId')
      }
    } finally {
      setPersonalIdLoading(false)
    }
  }

  useEffect(() => {
    if (values.mainIbCode && isASOrSC) {
      setHasIBCode(true)
    } else {
      setReferralCodeCollapsed(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (values.nationalityId) {
      getTruliooCountryData()
    }

    validateForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const isArabic = useArabicSessionLanguage()

  const handleReferralCodeCollapse = () => setReferralCodeCollapsed((prvState) => !prvState)

  const getCountryId = (): CountryCode | undefined => {
    return nationalities?.find((x) => x.id === values.nationalities?.[0]?.id)?.id
  }

  const handleProductChange = (productIds: number[]) => {
    let finalProductIds = productIds

    if (isStocksOnlyClient) {
      const previousProductIds = lead?.products?.map((p) => p.id) || []
      finalProductIds = [...new Set([...previousProductIds, ...productIds])]
    }

    setSelectedProductIds(finalProductIds)
    setFieldValue('productIds', finalProductIds)
  }

  const getNationalityLabel = (nationalities: string[], index: number) => {
    if (nationalities.length > 1) {
      return index === 0
        ? `${t('Sign up.Primary')}: ${nationalities[index]}`
        : `${t('Sign up.Secondary')}: ${nationalities[index]}`
    }

    return nationalities[index]
  }

  const isNextButtonDisabled = !!ibCodeError || personalIdLoading || isIbCodeValidating || !isValid

  const { regulatorOptions } = props
  const countryOptions = regulatorOptions?.map((regulatorOption) => regulatorOption.country)
  const [search, setSearch] = useState('')
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)
  const handleSearchClear = () => setSearch('')
  const filteredOptions = useMemo(
    () =>
      countryOptions
        ?.map((country) => {
          const countryCode = country.id as CountryCode
          return {
            name: country.name,
            id: country.id,
            startIcon: (({ size }: IconProps) => (
              <FlagIcon countryCode={countryCode} size={size} />
            )) as IconFCType,
          }
        })
        .filter((option) => option.name.toLowerCase().includes(search.toLowerCase())),
    [countryOptions, search]
  )

  const handleCountryChangeOption = (country: NameDto<string>) => {
    setFieldValue('nationalities', [...values.nationalities, country])
  }

  return (
    <Form className={styles.form} onSubmit={onSubmitForm}>
      <Box flex direction='column' gap='x16'>
        <ProductSelection
          products={availableProducts}
          productIds={values.productIds || []}
          onProductChange={handleProductChange}
          isLoading={productsLoading}
        />
      </Box>

      <Box flex py='x24' align='center' gap='x4'>
        <Heading6 medium>{t('Sign up.Nationality')}</Heading6>
        <Tooltip content={t('Sign up.Please confirm your nationality and place of birth.')}>
          <ActionIcon icon={InfoIcon} size='x20' />
        </Tooltip>
      </Box>

      <Box flex direction='column' gap='x12' fullWidth>
        <PopoverInput
          name='nationalityId'
          label={t('Sign up.Nationalities')}
          value={getCountryId()}
          required
          options={filteredOptions}
          searchInput={{
            value: search,
            handleChange: handleSearchChange,
            handleClear: handleSearchClear,
          }}
          onChange={handleCountryChangeOption}
          disabled={values.nationalities.length >= 2}
        />

        <Box flex wrap align='center' gap='x8'>
          {values.nationalities.map((n, index) => (
            <Chip
              key={n.id}
              selected
              size='sm'
              onDelete={(e) => {
                e.stopPropagation()
                onRemoveNation(n)
              }}
            >
              {getNationalityLabel(
                values.nationalities.map((x) => x.name),
                index
              )}
            </Chip>
          ))}
        </Box>
      </Box>

      {!!truliooCountryData?.length && (
        <div className='mt-3'>
          {truliooCountryData.map((countryData) => (
            <div key={countryData.id}>
              {truliooCountryData?.length > 1 && (
                <Radio
                  onChange={() => {
                    setFieldValue('personalId', undefined)
                    setFieldValue('personalIdTypeId', Number(countryData.id))
                    setFieldValue('selectedTruliooCountry', Number(countryData.id))
                  }}
                  checked={countryData.id === selectedTruliooCountry}
                >
                  {countryData.fieldName}
                </Radio>
              )}
              {countryData.id === selectedTruliooCountry && (
                <div className='mt-3 mb-3'>
                  <FormField
                    type='text'
                    required
                    name='personalId'
                    showLabel
                    wrapperClassname='mb-0'
                    placeholder={parseMixedText(countryData.placeholder)}
                    label={countryData.fieldName}
                    className={classNames({ [styles.personalId]: isArabic })}
                  />
                  <Caption1>{parseMixedText(countryData.fieldLabel)}</Caption1>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <Box flex py='x24' align='center' gap='x4'>
        <Heading6 medium>{t('Sign up.Residential address')}</Heading6>
      </Box>
      <ResidentialAddressFields countryId={lead?.country.id} />
      {requireTaxId && (
        <FieldWrapper>
          <Box flex py='x24' align='center' gap='x4' className={styles.headingRow}>
            <Heading6 medium>
              {t('Sign up.Tax Identification Number (TIN) / Goods and Services Tax Number (GSTN)')}
            </Heading6>
            <Tooltip
              content={
                <Body1
                  isParagraph
                  dangerouslySetInnerHTML={{
                    __html: t("Sign up.Here we'll need your individual Tax Identification Number", {
                      eu_link: links.TIN.EuropeanCommission,
                      worldwide_link: links.TIN.OECD,
                    }),
                  }}
                />
              }
            >
              <ActionIcon icon={InfoIcon} size='x20' />
            </Tooltip>
          </Box>
          <FormField
            name='taxId'
            label={t('Sign up.TIN / GSTN')}
            type='text'
            disabled={values.taxIdNotAvailable}
            required
            showLabel
          />
          <div className={classNames(sharedStyles.textSmall, 'control')}>
            <FormCheckBoxField
              name='taxIdNotAvailable'
              onChange={(checked) => onChangeTaxAvailability(checked)}
            >
              {t('Sign up.Not Available')}
            </FormCheckBoxField>
          </div>
          {values.taxIdNotAvailable &&
            taxReasons.map((type) => (
              <Radio
                key={type.id}
                name='taxIdUnavailableReasonId'
                checked={values.taxIdUnavailableReasonId === type.id}
                required
                onChange={() => setFieldValue('taxIdUnavailableReasonId', type.id)}
              >
                {type.name}
              </Radio>
            ))}
        </FieldWrapper>
      )}
      <Box flex py='x24' align='center' gap='x4'>
        <Heading6 medium>{t('Sign up.Politically Exposed Person.Title')}</Heading6>
        <ActionIcon
          icon={InfoIcon}
          size='x20'
          onClick={() => pepInfoModal.open()}
          aria-label={t('Sign up.Politically Exposed Person.Title')}
        />
      </Box>
      <div className={classNames(sharedStyles.textSmall, 'control')}>
        <FormCheckBoxField name='isPoliticallyExposedPerson'>
          {t('Sign up.Politically Exposed Person.Im Politically Exposed Person')}
        </FormCheckBoxField>
      </div>

      {isASOrSC && !referral.isAffiliatesParams() && (
        <Box pt='x24' flex direction='column' gap='x24'>
          <Box flex align='center' gap='x4' onClick={handleReferralCodeCollapse}>
            {referralCodeCollapsed ? <ChevronIcon.Up /> : <ChevronIcon.Down />}
            <Box flex align='center' gap='x4'>
              <Body1>{t('Sign up.Referral Code')}</Body1>
              <Tooltip
                content={
                  <Body1
                    isParagraph
                    dangerouslySetInnerHTML={{
                      __html: hasIBCode
                        ? t(
                            'Sign up.Referrals are agents who introduce new clients to Tickmill. The code has already been provided to you by your Referral. No further action is required'
                          )
                        : t('Sign up.Referrals are agents who introduce new clients to Tickmill.') +
                          ' ' +
                          t(
                            'Sign up.Enter the code that was provided to you by your Referral. The code consists of a combination of letters and digits.'
                          ),
                    }}
                  />
                }
              >
                <ActionIcon icon={InfoIcon} size='x20' />
              </Tooltip>
            </Box>
          </Box>

          {referralCodeCollapsed && (
            <FormField
              disabled={!!initialValues.mainIbCode && hasIBCode && !ibCodeError}
              type='text'
              label={hasIBCode ? '' : `${t('Sign up.Enter IB Code')}...`}
              showLabel={false}
              validateOnType
              required={false}
              error={ibCodeError || errors.mainIbCode}
              name='mainIbCode'
            />
          )}
        </Box>
      )}
      <div className={styles.button}>
        <Button.Primary
          size='lg'
          type='submit'
          fullWidth
          iconRight={ArrowIcon.Right}
          loading={isSubmitting}
          disabled={isNextButtonDisabled}
        >
          {t('Next')}
        </Button.Primary>
      </div>
    </Form>
  )
}

export const PersonalDetailsStep4Form = withFormik<OuterProps, PersonalDetailsStep4FormValues>({
  mapPropsToValues: ({ initialValues }) => {
    return {
      nationalities: initialValues?.nationalities || [],
      primaryAddressCity: initialValues?.primaryAddressCity || '',
      primaryAddressState: initialValues?.primaryAddressState || '',
      address: initialValues?.address || '',
      primaryAddressPostalCode: initialValues?.primaryAddressPostalCode || '',
      taxId: initialValues?.taxId || '',
      mainIbCode: initialValues?.mainIbCode || '',
      affiliateToken: initialValues?.affiliateToken || '',
      taxIdNotAvailable: initialValues?.taxIdNotAvailable || false,
      taxIdUnavailableReasonId: initialValues?.taxIdUnavailableReasonId ?? undefined,
      isPoliticallyExposedPerson: initialValues?.isPoliticallyExposedPerson || false,
      nationalityId: initialValues?.nationalityId || '',
      personalId: initialValues?.personalId || '',
      truliooCountryData: initialValues?.truliooCountryData || [],
      personalIdTypeId: initialValues?.personalIdTypeId || null,
      streetHouseNumberNotAvailable: initialValues?.streetHouseNumberNotAvailable ?? false,
      streetHouseNumber: initialValues?.streetHouseNumber ?? '',
      addressEntryMode: getInitialAddressEntryMode(initialValues),
      productIds: initialValues?.productIds || [],
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)

      let finalProductIds = values.productIds || []

      if (props.isStocksOnlyClient) {
        const previousProductIds = props.lead?.products?.map((p) => p.id) || []
        finalProductIds = [...new Set([...previousProductIds, ...finalProductIds])]
      }

      await props.onSubmit({
        ...values,
        productIds: finalProductIds,
      })
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, { entity, requireTaxId, country, isStocksOnlyClient, lead }) => {
    const errors: FormikErrors<PersonalDetailsStep4FormValues> = {}

    if (isStocksOnlyClient) {
      const selectedProductIds = values.productIds || []
      const previousProductIds = lead?.products?.map((p) => p.id) || []

      const hasNewProductSelection = selectedProductIds.some(
        (id) => !previousProductIds.includes(id)
      )

      if (!hasNewProductSelection || selectedProductIds.length === 0) {
        errors.productIds = t('Validation.Please select product(s)')
      }
    }

    if (values.truliooCountryData.length) {
      const truliooCountry = values.truliooCountryData.find(
        ({ id }) => id === values.selectedTruliooCountry
      )
      if (truliooCountry && country?.id === truliooCountry.country.id) {
        if (!values.personalId) {
          errors.personalId = t('Validation.Required')
        } else if (!new RegExp(truliooCountry?.validationRule).test(values.personalId)) {
          errors.personalId = t('Validation.Invalid format')
        }
      }
    }

    if (!values.nationalityId.length) {
      errors.nationalityId = t('Validation.Required')
    }

    if (values.addressEntryMode === AddressEntryMode.Lookup) {
      errors.address = values.address.trim()
        ? t('Sign up.Please select an address from the dropdown or enter it manually')
        : t('Validation.Required')
    } else {
      Object.assign(
        errors,
        validateManualAddressFields(
          values,
          {
            address: 'address',
            city: 'primaryAddressCity',
            state: 'primaryAddressState',
            postalCode: 'primaryAddressPostalCode',
            streetHouseNumber: 'streetHouseNumber',
          },
          {
            streetHouseNumber: {
              invalidMessageKey: 'Validation.Invalid street',
              streetHouseNumberNotAvailable: values.streetHouseNumberNotAvailable,
            },
          }
        )
      )
    }

    if (requireTaxId && !values.taxId.trim() && !values.taxIdNotAvailable) {
      errors.taxId = t('Validation.Required')
    }

    if (
      entity === TickmillCompaniesEnum.TICKMILL_AS ||
      entity === TickmillCompaniesEnum.TICKMILL_SC
    ) {
      if (values.mainIbCode && values.mainIbCode.length <= 5) {
        errors.mainIbCode = t('Validation.At least characters', {
          length: 6,
        })
      }
      if (values.mainIbCode && !IBPattern.test(values.mainIbCode)) {
        errors.mainIbCode = t('Validation.IB code should begin with')
      }
    }

    if (values.taxIdNotAvailable && !values.taxIdUnavailableReasonId) {
      errors.taxIdUnavailableReasonId = t('Validation.Required')
    }

    return errors
  },
  validateOnMount: true,
  enableReinitialize: true,
})(PersonalDetailsStep4FormUI)
